import React, { useLayoutEffect, useRef, useState } from "react";

// The brand colour an element draws in (its buttons, the selected date, the
// Stripe form's accent) when its page says nothing: Baluu's teal, the value
// every root component defaulted its `primaryColor` prop to.
export const DEFAULT_PRIMARY = "#03c2b9";

/** The host's brand variable. A page sets it on the element from outside
 * (Website v2's bookingComponentCss, with the site's whole palette) and
 * globals.css defaults it on :host, so it always has a value. An element's
 * `primary-color` attribute is written to it too, inline on the host, so
 * the attribute keeps beating the page and the hover shade derived from
 * the variable in globals.css follows it. */
export const HOST_PRIMARY_VARIABLE = "--checkout-primary";

type Computed = (element: Element) => CSSStyleDeclaration;

/** A colour as "#rrggbb", lower case; null when `value` isn't one the code
 * downstream can take: the checkout's hover shade and the Stripe form's
 * appearance both need a hex. A page's variable comes back from
 * getComputedStyle as written, so a hex (what bookingComponentCss emits)
 * or an rgb() is read; anything else leaves the caller its default while
 * the CSS-driven parts still follow the variable. */
export function hexOf(value: string | null | undefined): string | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const digits = hex[1]!.toLowerCase();
    return digits.length === 3 ? `#${[...digits].map((d) => d + d).join("")}` : `#${digits}`;
  }
  const rgb = raw.match(/^rgb\(\s*(\d+)\s*[, ]\s*(\d+)\s*[, ]\s*(\d+)\s*\)$/i);
  if (rgb) {
    const channel = (n: string) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, "0");
    return `#${channel(rgb[1]!)}${channel(rgb[2]!)}${channel(rgb[3]!)}`;
  }
  return null;
}

/** The element whose shadow tree holds `node`, or null outside one. */
export function hostOf(node: Element | null): HTMLElement | null {
  const root = node?.getRootNode();
  return root instanceof ShadowRoot && root.host instanceof HTMLElement ? root.host : null;
}

/** The brand colour of the element whose shadow tree holds `node`: its
 * `--checkout-primary` as the browser computes it on the host, as a hex.
 * Null outside a shadow tree, or when the variable holds nothing usable. */
export function readHostPrimary(node: Element | null, computed: Computed = (el) => getComputedStyle(el)): string | null {
  const host = hostOf(node);
  return host ? hexOf(computed(host).getPropertyValue(HOST_PRIMARY_VARIABLE)) : null;
}

/** Gives a root component its `primaryColor` from the host element when the
 * page passed no `primary-color` attribute: a v2 page's theme reaches the
 * component's inline colours (and the Stripe form, outside the shadow
 * root) the way its CSS variables already reach the shadcn classes. With
 * nothing set on the host, globals.css's :host default resolves to
 * DEFAULT_PRIMARY, so an embed looks as it did. The component mounts once
 * the colour is known (a layout effect, before the first paint), so nothing
 * draws in the default and then changes.
 *
 * An attribute that is given goes the other way, onto the host as the
 * variable, so the stylesheet's uses of it (the selected session card, the
 * focus ring, the hover shade) follow the attribute as they did when the
 * checkout wrote them inline. */
export function withHostBrand<P extends { primaryColor?: string }>(Component: React.ComponentType<P>): React.ComponentType<P> {
  function HostBrand(props: P) {
    const probe = useRef<HTMLSpanElement>(null);
    const [resolved, setResolved] = useState<string | null>(null);
    const explicit = props.primaryColor;
    useLayoutEffect(() => {
      const host = hostOf(probe.current);
      if (explicit) {
        host?.style.setProperty(HOST_PRIMARY_VARIABLE, explicit);
        return () => {
          host?.style.removeProperty(HOST_PRIMARY_VARIABLE);
        };
      }
      setResolved(readHostPrimary(probe.current) ?? DEFAULT_PRIMARY);
    }, [explicit]);
    const marker = React.createElement("span", { ref: probe, hidden: true, "data-host-brand-probe": "" });
    if (!explicit && resolved === null) return marker;
    return React.createElement(
      React.Fragment,
      null,
      marker,
      React.createElement(Component, explicit ? props : { ...props, primaryColor: resolved! }),
    );
  }
  HostBrand.displayName = `withHostBrand(${Component.displayName || Component.name || "Component"})`;
  return HostBrand;
}
